import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, ChevronLeft, ChevronRight, Search, Pencil, Eye, Loader2, X } from "lucide-react";
import { adminApi } from "../api";
import AdminQuickSearch, { getQuickSearchConfig } from "../components/AdminQuickSearch";

const PAGE_SIZES = [12, 25, 50, 100];

function formatCell(v) {
  if (v === null || v === undefined) return <span className="text-[#9CA3AF]">—</span>;
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  if (typeof v === "string" && v.length > 80) return v.slice(0, 80) + "…";
  return String(v);
}

function formatFkCell(id, label) {
  if (id === null || id === undefined) return <span className="text-[#9CA3AF]">—</span>;
  if (!label) return String(id);
  const short = String(label).length > 50 ? String(label).slice(0, 50) + "…" : String(label);
  // Always show the ID alongside the label — bugs like "ID_Encomenda" showing
  // "FE" instead of "70434" (caso #70434) ficam imediatamente visíveis em vez
  // de mascarados pela label resolvida heuristicamente.
  return <span title={`${label} (#${id})`}><span className="font-mono text-[#0F1829]">{String(id)}</span> <span className="text-[#6B7280]">— {short}</span></span>;
}

export default function AdminTable() {
  const { schema, name } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Pre-filter from query string (e.g. ?fcol=ID_Cliente&fval=123 coming from a
  // "Registos relacionados" button).
  const preFilterCol = searchParams.get("fcol");
  const preFilterVal = searchParams.get("fval");

  const loadMeta = useCallback(async () => {
    setMeta(null);
    try {
      const m = await adminApi.tableSchema(schema, name);
      setMeta(m);
      setSortKey(m.pk);
    } catch (e) {
      setError(e.message || "Erro a carregar schema");
    }
  }, [schema, name]);

  const loadRows = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await adminApi.tableList(schema, name, {
        page, page_size: pageSize, search: debouncedSearch, sort_key: sortKey, sort_dir: sortDir, filters,
      });
      setRows(r.data || []);
      // Backend só devolve total na página 1 (optimização). Nas seguintes,
      // preservamos o valor conhecido para o pagination continuar a funcionar.
      if (typeof r.total === "number") setTotal(r.total);
    } catch (e) {
      setError(e.message || "Erro a carregar dados");
      setRows([]);
    } finally { setLoading(false); }
  }, [schema, name, page, pageSize, debouncedSearch, sortKey, sortDir, filters]);

  useEffect(() => { loadMeta(); setPage(1); setSearch(""); setDebouncedSearch(""); }, [loadMeta]);
  // Debounce da caixa de pesquisa: só faz query 350ms após a última tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  // Seed filters from query string whenever table changes
  useEffect(() => {
    if (preFilterCol && preFilterVal) {
      setFilters({ [preFilterCol]: preFilterVal });
    } else {
      setFilters({});
    }
  }, [schema, name, preFilterCol, preFilterVal]);
  useEffect(() => { if (meta) loadRows(); }, [meta, loadRows]);

  if (!meta) {
    return <div className="text-sm text-[#4B5563]">A carregar estrutura da tabela...</div>;
  }

  const canCreate = (meta.ops || []).includes("create");
  const canEdit = (meta.ops || []).includes("edit");
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Show ALL non-blob columns and rely on horizontal scroll for wide tables.
  const displayCols = (meta.columns || [])
    .filter((c) => !["text", "ntext", "image", "varbinary"].includes((c.DATA_TYPE || "").toLowerCase()));

  const fkCols = new Set((meta.fks || []).map((f) => f.column));

  const toggleSort = (col) => {
    if (sortKey === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(col); setSortDir("asc"); }
  };

  return (
    <div data-testid="admin-table-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }} data-testid="admin-table-label">{meta.label}</h1>
          <p className="text-xs font-mono text-[#978D78] mt-0.5">{schema}.{name} · PK: <strong>{meta.pk || "—"}</strong> · <span className="uppercase tracking-[0.15em] text-[10px]">{(meta.ops || []).join(" / ")}</span></p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <button onClick={() => nav(`/admin/t/${schema}/${name}/new`)} data-testid="admin-create-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0F1829] text-white text-sm rounded hover:bg-[#001f6e] transition">
              <Plus size={14} /> Novo registo
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#978D78]" />
          <input
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Pesquisar texto nas colunas..."
            data-testid="admin-search-input"
            className="pl-8 pr-3 py-1.5 text-sm border border-[#E6E1D6] rounded w-72 outline-none focus:border-[#0F1829]"
          />
        </div>
        <select value={pageSize} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}
          data-testid="admin-page-size"
          className="px-2 py-1.5 text-sm border border-[#E6E1D6] rounded outline-none">
          {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}/pg</option>)}
        </select>
        {(search || Object.keys(filters).some((k) => filters[k])) && (
          <button onClick={() => { setSearch(""); setFilters({}); setSearchParams({}); setPage(1); }}
            data-testid="admin-clear-filters"
            className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-[#0F1829] border border-[#E6E1D6] rounded hover:bg-[#F5F2EB]">
            <X size={12} /> Limpar filtros
          </button>
        )}
        <span className="text-xs text-[#6B7280] ml-auto" data-testid="admin-total-count">{total.toLocaleString("pt-PT")} registos</span>
      </div>

      {preFilterCol && preFilterVal && (
        <div data-testid="admin-prefilter-banner" className="mb-3 px-3 py-2 bg-[#FAF6ED] border border-[#E6E1D6] text-xs text-[#4B5563] rounded flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#978D78]">Filtro activo</span>
          <span className="font-mono">{preFilterCol} = {preFilterVal}</span>
          <button onClick={() => { setSearchParams({}); setFilters({}); setPage(1); }}
            className="ml-auto inline-flex items-center gap-1 text-[#0F1829] hover:underline">
            <X size={11} /> Remover
          </button>
        </div>
      )}

      {(() => {
        const qs = getQuickSearchConfig(schema, name);
        if (!qs) return null;
        return (
          <AdminQuickSearch
            config={qs}
            currentFilters={filters}
            onApply={(vals) => { setPage(1); setSearch(""); setFilters(vals); }}
            onClear={() => { setPage(1); setFilters({}); setSearch(""); setSearchParams({}); }}
          />
        );
      })()}

      {error && <div data-testid="admin-error" className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded">{error}</div>}

      <div className="bg-white border border-[#E6E1D6] rounded-md overflow-x-auto max-w-full">
        <table className="text-sm" style={{ minWidth: "100%", width: "max-content" }}>
          <thead className="bg-[#F5F2EB] border-b border-[#E6E1D6]">
            <tr>
              <th className="w-16 px-2"></th>
              {displayCols.map((c) => (
                <th key={c.COLUMN_NAME} onClick={() => toggleSort(c.COLUMN_NAME)}
                  className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78] cursor-pointer hover:text-[#0F1829] select-none whitespace-nowrap">
                  {c.COLUMN_NAME}{sortKey === c.COLUMN_NAME && <span className="ml-1">{sortDir === "asc" ? "▲" : "▼"}</span>}
                </th>
              ))}
            </tr>
            <tr className="bg-white border-t border-[#E6E1D6]">
              <th className="px-2 py-1"></th>
              {displayCols.map((c) => (
                <th key={c.COLUMN_NAME} className="px-1.5 py-1 whitespace-nowrap">
                  <input
                    type="text"
                    value={filters[c.COLUMN_NAME] ?? ""}
                    onChange={(e) => { setPage(1); setFilters((prev) => ({ ...prev, [c.COLUMN_NAME]: e.target.value })); }}
                    placeholder="filtrar..."
                    data-testid={`admin-filter-${c.COLUMN_NAME}`}
                    className="w-full px-2 py-1 text-[11px] font-mono border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none bg-[#FAF8F2]"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={displayCols.length + 1} className="text-center py-8 text-[#6B7280]">
                <Loader2 size={18} className="animate-spin inline mr-1" /> A carregar...
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={displayCols.length + 1} className="text-center py-8 text-[#6B7280]">Sem registos.</td></tr>
            )}
            {!loading && rows.map((r, i) => (
              <tr key={`${r[meta.pk] ?? i}`} data-testid={`admin-row-${r[meta.pk] ?? i}`} className="border-b border-[#F3F0E7] hover:bg-[#FAF8F2]">
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <button onClick={() => nav(`/admin/t/${schema}/${name}/view/${encodeURIComponent(r[meta.pk])}`)}
                      data-testid={`admin-view-${r[meta.pk] ?? i}`}
                      className="text-[#0F1829] hover:text-[#978D78] p-0.5" title="Ver detalhe">
                      <Eye size={14} />
                    </button>
                    {canEdit && (
                      <button onClick={() => nav(`/admin/t/${schema}/${name}/${encodeURIComponent(r[meta.pk])}`)}
                        data-testid={`admin-edit-${r[meta.pk] ?? i}`}
                        className="text-[#0F1829] hover:text-[#978D78] p-0.5" title="Editar">
                        <Pencil size={14} />
                      </button>
                    )}
                  </div>
                </td>
                {displayCols.map((c) => (
                  <td key={c.COLUMN_NAME} className="px-3 py-1.5 whitespace-nowrap">
                    {fkCols.has(c.COLUMN_NAME)
                      ? formatFkCell(r[c.COLUMN_NAME], r[`${c.COLUMN_NAME}__label`])
                      : formatCell(r[c.COLUMN_NAME])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-center gap-2 mt-3 text-sm">
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
          data-testid="admin-prev-page"
          className="p-1.5 rounded border border-[#E6E1D6] disabled:opacity-30 hover:bg-[#F5F2EB]"><ChevronLeft size={14} /></button>
        <span data-testid="admin-page-info" className="text-xs text-[#6B7280]">Pág {page} / {totalPages}</span>
        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
          data-testid="admin-next-page"
          className="p-1.5 rounded border border-[#E6E1D6] disabled:opacity-30 hover:bg-[#F5F2EB]"><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}
